export const SYRACUSE_CENTER = {
  lat: 43.04516677500132,
  lng: -76.10151737324254,
};

export const SYRACUSE_RADIUS_KM = 45;

export const distanceKm = (a, b) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

export const isNearSyracuse = (location) =>
  distanceKm(location, SYRACUSE_CENTER) <= SYRACUSE_RADIUS_KM;

export const GROCERY_STAPLES = [
  { key: 'eggs12', emoji: '🥚', label: 'Eggs (dozen)' },
  { key: 'milkGallon', emoji: '🥛', label: 'Milk (gallon)' },
  { key: 'chickenLb', emoji: '🍗', label: 'Chicken (lb)' },
  { key: 'beefLb', emoji: '🥩', label: 'Ground beef (lb)' },
];

const EMPTY_STAPLE_PRICES = Object.fromEntries(
  GROCERY_STAPLES.map(({ key }) => [key, '—'])
);

const priceLevelLabel = (level) => {
  if (level == null || level === 0) return '';
  return '$'.repeat(Math.min(level, 4));
};

const placeToRestaurant = (place) => ({
  placeId: place.place_id,
  name: place.name,
  location: {
    lat: place.geometry.location.lat(),
    lng: place.geometry.location.lng(),
  },
  address: place.vicinity || place.formatted_address || '',
  special: place.rating ? `★ ${place.rating} (${place.user_ratings_total || 0} reviews)` : 'Local restaurant',
  specialinfo: place.opening_hours?.open_now != null
    ? (place.opening_hours.open_now ? 'Open now' : 'Closed now')
    : 'From Google Maps',
  price: priceLevelLabel(place.price_level) || '—',
  phone: '',
  isNearby: true,
});

const placeToGrocery = (place) => ({
  placeId: place.place_id,
  name: place.name,
  location: {
    lat: place.geometry.location.lat(),
    lng: place.geometry.location.lng(),
  },
  address: place.vicinity || place.formatted_address || '',
  special: place.rating ? `★ ${place.rating}` : 'Nearby grocery store',
  specialinfo: place.opening_hours?.open_now != null
    ? (place.opening_hours.open_now ? 'Open now' : 'Closed now')
    : 'From Google Maps',
  ...EMPTY_STAPLE_PRICES,
  phone: '',
  isNearby: true,
});

export const fetchNearbyPlaces = (center, viewMode) =>
  new Promise((resolve, reject) => {
    if (!window.google?.maps?.places) {
      reject(new Error('Google Places is not loaded'));
      return;
    }

    const div = document.createElement('div');
    const service = new window.google.maps.places.PlacesService(div);
    const type = viewMode === 'grocery' ? 'supermarket' : 'restaurant';

    service.nearbySearch(
      {
        location: center,
        radius: 12000,
        type,
      },
      (results, status) => {
        const ok = window.google.maps.places.PlacesServiceStatus.OK;
        if (status !== ok || !results?.length) {
          reject(new Error(status || 'No nearby places found'));
          return;
        }

        const mapped = results
          .filter((place) => place.geometry?.location)
          .map((place) => (viewMode === 'grocery' ? placeToGrocery(place) : placeToRestaurant(place)));

        resolve(mapped);
      }
    );
  });

export const getUserLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        source: 'device',
      }),
      (err) => reject(err),
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000 }
    );
  });

/** IP-based location — follows VPN exit node, not device GPS. */
export const getNetworkLocation = () =>
  fetch('https://ipwho.is/')
    .then((res) => res.json())
    .then((data) => {
      if (!data.success || data.latitude == null || data.longitude == null) {
        throw new Error('Network location lookup failed');
      }
      const label = [data.city, data.region].filter(Boolean).join(', ');
      return {
        lat: data.latitude,
        lng: data.longitude,
        source: 'network',
        label: label || 'your network location',
      };
    });

export const resolveBestLocation = (mode = 'auto') => {
  if (mode === 'device') {
    return getUserLocation();
  }
  if (mode === 'network') {
    return getNetworkLocation();
  }

  return Promise.all([
    getUserLocation().catch(() => null),
    getNetworkLocation().catch(() => null),
  ]).then(([device, network]) => {
    if (
      device
      && isNearSyracuse(device)
      && network
      && !isNearSyracuse(network)
    ) {
      return {
        ...network,
        source: 'network',
        viaVpn: true,
      };
    }
    if (device) return device;
    if (network) return network;
    throw new Error('No location available');
  });
};